import type { GeoLocation } from '@openx/types/geoLocation.ts';
import type { GeoSearchOutput } from '@openx/types/gqlTypes.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import { GeoSearchDocument } from '../../../../graphql/query/geo/search.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../../modules/graphql/types.ts';

export const usePostalCodes = () => {
  const queryClient = useQueryClient();

  const fetchPostalCodes = async (param: { country: string; postal_codes: string[] }) => {
    const { country, postal_codes } = param;

    const postalCodesWithQuotes = postal_codes.map(code => {
      const trimmed = code.trim();

      return trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed : `"${trimmed}"`;
    });

    const phrase = `(type:postal_code AND country:(${country}) AND postal_code:(${postalCodesWithQuotes.join(' OR ')}))`;
    const variables = { limit: postal_codes.length, phrase };

    // graphQLRequest already reports the error; resolve to an empty result like the former Apollo lazy query did
    const response = await queryClient
      .fetchQuery({
        queryKey: [QueryKeys.SEARCH, 'geo', variables],
        queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GeoSearchDocument, variables),
      })
      .catch(() => null);

    if (!response) {
      return [];
    }

    const geoRes: GeoSearchOutput[] = response?.geoSearch ?? ([] as GeoSearchOutput[]);

    return geoRes.map(item => {
      const obj: GeoLocation = {
        city: item.city,
        code: item.code,
        continent: item.continent,
        country: item.country,
        dma: item.dma,
        id: item.id,
        latitude: `${item.latitude}`,
        longitude: `${item.longitude}`,
        msa: item.msa,
        name: item.name,
        postal_code: item.postal_code,
        region: item.region,
        state: item.state,
        type: item.type,
        'type-id': item.type_id,
      };

      return obj;
    });
  };

  return { fetchPostalCodes };
};
